"use client";

import { usePageState, useParseMeta, useProductCardHighlight } from "@2u-landing-pages/providers";
import { useModal } from "@2u-landing-pages/providers/ModalProvider";
import { IconSizes, TypeButtonWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { renderText } from "@2u-landing-pages/utils/contentful-richtext";
import {
  dispatchEduListingFromForm,
  isResolvedFormEntry,
} from "@2u-landing-pages/utils/buildEduListingPayloadFromForm";
import EventClient from "@2u-landing-pages/utils/event-client";
import { scrollToElementById } from "@2u-landing-pages/utils/scrollToElement";
import { TypeFormWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { getIconSelectionFromFields } from "@2u-landing-pages/utils/icon-selection";
import type { ButtonProps as BricksButtonProps } from "@red-digital/bricks";
import { Button as BricksButton } from "@red-digital/bricks";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useCalendly } from "../CalendlyWidget/useCalendly";
import { ContentfulImage } from "../ContentfulImage";
import { Icon } from "../Icon";
// @ts-ignore
import styles from "./Button.module.scss";

type ButtonProps = Omit<BricksButtonProps, "icon" | "form"> &
  Omit<TypeButtonWithoutUnresolvableLinksResponse["fields"], "name" | "action" | "icon" | "form"> & {
    iconSelection?: unknown;
    action?: TypeButtonWithoutUnresolvableLinksResponse["fields"]["action"];
    forceDarkLabel?: boolean;
    location?: string;
    drawerType?: "multi-program" | "single-program";
    form?: TypeFormWithoutUnresolvableLinksResponse;
    phoneNumber?: string;
    icon?: BricksButtonProps["icon"] | string;
  };

const iconSizeMap = {
  small: "sm",
  medium: "md",
  large: "lg",
} as const;

export const Button: React.FC<ButtonProps> = ({
  forceDarkLabel,
  location = "",
  iconSelection,
  icon_mobile_only,
  children,
  icon,
  action,
  onClick,
  logo,
  variation,
  link_url,
  show_dropshadow,
  persist_query_params = false,
  drawerType = "single-program",
  form,
  phoneNumber,
  ...rest
}) => {
  const buttonProps = rest as BricksButtonProps;

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia(`(max-width: 1024px)`).matches : false,
  );
  const { icon: selectedIcon } = getIconSelectionFromFields({ iconSelection }, []);
  const { dispatch, primaryButtonDarkLabel } = usePageState();
  const { setHighlightedCardId } = useProductCardHighlight();
  const parseMetadata = useParseMeta();
  const openModal = useModal()?.openModal;
  const hasLogo = logo?.fields?.file?.url;

  const labelText =
    typeof children === "string"
      ? children
      : typeof children === "object"
        ? children?.toString().replace(/,/g, "")
        : children?.toString() || "";

  const buttonLabels = useMemo(() => {
    const label = labelText
      ? renderText(parseMetadata ? parseMetadata(labelText) : labelText, phoneNumber).toString()
      : "";
    return { "aria-label": label, label };
  }, [labelText, parseMetadata, phoneNumber]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(`(max-width: 1024px)`);

    const handleScreenChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };

    mediaQuery.addEventListener("change", handleScreenChange);

    return () => {
      mediaQuery.removeEventListener("change", handleScreenChange);
    };
  }, []);

  const isCalendlyLink = link_url?.includes("calendly");

  const { openCalendlyPopup } = useCalendly(isCalendlyLink);

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (action) {
      if (action === "OPEN_LINK") {
        if (link_url) {
          let finalLinkUrl = link_url;

          if (persist_query_params) {
            const url = new URL(link_url);
            const queryParams = new URLSearchParams(window.location.search);

            queryParams.forEach((value, key) => {
              url.searchParams.set(key, value);
            });

            finalLinkUrl = url.toString();
          }

          window.open(finalLinkUrl, "_blank");
        }
      } else if (action === "SLIDE_TO_ELEMENT") {
        if (link_url) {
          scrollToElementById(link_url);
        }
      } else if (action === "SCROLL_HIGHLIGHT_PRODUCT_CARD") {
        if (link_url) {
          scrollToElementById(link_url);
          setHighlightedCardId(link_url);
        }
      } else if (action === "BACK_TO_TOP") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (action === "OPEN_DRAWER") {
        const linkedForm = isResolvedFormEntry(form) ? form : undefined;
        const effectiveDrawerType = linkedForm ? "multi-program" : drawerType;

        if (linkedForm) {
          try {
            dispatchEduListingFromForm(linkedForm);
          } catch (error) {
            console.error("Error creating drawer event data:", error);
          }
        }

        dispatch({ type: action, drawerType: effectiveDrawerType });
      } else if (action === "OPEN_MODAL") {
        if (link_url && openModal) openModal(link_url as string);
      } else if (action === "OPEN_CALENDLY") {
        if (isCalendlyLink && openCalendlyPopup) openCalendlyPopup(link_url as string);
      } else {
        dispatch({ type: action });
      }
    }
    if (onClick) onClick(event);

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location,
        position: "CTA",
        text: children?.toString() || selectedIcon || link_url || "",
      },
    });
  };

  const buttonColor = typeof buttonProps?.color === "string" ? buttonProps.color : "";
  const primaryColor = buttonColor.includes("primary") || !buttonColor;

  return (
    <>
      <BricksButton
        className={cn(styles.button, {
          [styles.darkLabel]: (primaryButtonDarkLabel && primaryColor) || forceDarkLabel,
          [styles.dropshadow]: show_dropshadow,
          [styles.btnWithLogo]: hasLogo,
          "b-btn--icon-only": ((isMobile && icon_mobile_only) || !children) && !hasLogo,
        })}
        role="button"
        v2
        {...buttonProps}
        {...buttonLabels}
        variant={variation ?? buttonProps.variant}
        onClick={handleOnClick}
        icon={
          selectedIcon
            ? {
                component: (
                  <Icon
                    name={selectedIcon}
                    size={(iconSizeMap[buttonProps.size as keyof typeof iconSizeMap] ?? "lg") as IconSizes}
                  />
                ),
                position: "left",
              }
            : typeof icon === "string"
              ? undefined
              : icon
        }
      >
        {hasLogo ? (
          <ContentfulImage image={logo} className={styles.logo} loading="lazy" />
        ) : (
          <>
            {isMobile && icon_mobile_only ? undefined : buttonLabels.label}
            {action === "OPEN_LINK" && (
              <Icon name="external-link" style={{ fontSize: "1em" }} className={styles.externalLink} />
            )}
          </>
        )}
      </BricksButton>
    </>
  );
};
